import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LayoutGrid } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SiteCard from '../components/SiteCard';
import CategoryBadge from '../components/CategoryBadge';
import { Container } from '../components/styled/Container';
import { PageLayout } from '../components/styled/PageLayout';
import { fetchCategories, fetchSites } from '../store/actions/siteActions';
import SeoHead from '../components/SeoHead';
import AdBanner from '../components/ads/AdBanner';
import AdInline from '../components/ads/AdInline';
import { bannerAds, cardAds } from '../data/adData';
import styled from 'styled-components';

const Header = styled(motion.div)`
  margin-bottom: 1.5rem;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
`;

const IconBox = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: rgba(128, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.maroon};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.fontClamp['3xl']};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0 0 0 3.25rem;
`;

const BadgesWrap = styled.div`
  margin-top: 1.5rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const Grid = styled.div`
  margin-top: 2rem;
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (min-width: 1400px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const Empty = styled.div`
  text-align: center;
  padding: 5rem 0;
`;

function Categories() {
  const dispatch = useDispatch();
  const { category: categoryParam } = useParams();
  /** URL segment: slug (preferred) or legacy exact name */
  const activeSlug = categoryParam ? decodeURIComponent(categoryParam) : null;
  const { items, categories, loading, error } = useSelector((state) => state.sites);

  const activeMeta = categories.find(
    (c) => c.slug === activeSlug || c.name === activeSlug
  );
  const displayTitle = activeMeta?.name || activeSlug;

  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchSites({ category: activeSlug || undefined, limit: 50 }));
  }, [dispatch, activeSlug]);

  return (
    <PageLayout>
      <SeoHead
        title={displayTitle ? `${displayTitle} sites | CiteMatch` : 'Categories | CiteMatch'}
        description={displayTitle ? `Browse sites in the ${displayTitle} category.` : 'Explore websites organized by category.'}
      />
      <Navbar />
      <Container style={{ paddingTop: '2.5rem', paddingBottom: '2.5rem' }}>
        <Header initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <HeaderRow>
            <IconBox>
              <LayoutGrid size={20} strokeWidth={2.5} />
            </IconBox>
            <Title>{displayTitle || 'All Categories'}</Title>
          </HeaderRow>
          <Subtitle>
            {displayTitle
              ? `Browse ${items.length} sites in ${displayTitle}`
              : 'Explore websites organized by category'}
          </Subtitle>
        </Header>

        <BadgesWrap>
          <CategoryBadge name="All" active={!activeSlug} />
          {categories.map((cat) => (
            <CategoryBadge
              key={cat.slug || cat._id || cat.name}
              name={cat.name}
              slug={cat.slug}
              active={activeSlug === cat.slug || activeSlug === cat.name}
            />
          ))}
        </BadgesWrap>

        <div style={{ marginTop: '1.5rem' }}>
          <AdBanner ad={bannerAds[0]} variant="inline" />
        </div>

        <Grid style={{ marginTop: '2rem' }}>
          {items.slice(0, 3).map((site) => (
            <SiteCard key={site.id} site={site} />
          ))}
        </Grid>

        {items.length > 3 && (
          <div style={{ margin: '1.5rem 0' }}>
            <AdInline ad={cardAds[1]} />
          </div>
        )}

        <Grid>
          {items.slice(3).map((site) => (
            <SiteCard key={site.id} site={site} />
          ))}
        </Grid>

        {error && <div style={{ color: 'var(--crimson)', marginTop: '1rem' }}>{error}</div>}

        {!loading && items.length === 0 && (
          <Empty>
            <p style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>No sites in this category yet</p>
            <p style={{ color: 'var(--muted-foreground)', marginTop: '0.5rem' }}>Check back later or submit a site!</p>
          </Empty>
        )}
      </Container>
      <Footer />
    </PageLayout>
  );
}

export default Categories;
